import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { blogPosts } from '../data/blogPosts';
import { Clock, ArrowLeft, ArrowRight, AlertCircle, Lightbulb, Calendar, Share2 } from 'lucide-react';

// Helper function to render text with bold formatting
const renderFormattedText = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <strong key={index} className="font-bold text-slate-900">{boldText}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeSection, setActiveSection] = useState<string>('');
  const { scrollY } = useScroll();

  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 400], [1, 0.95]);

  const post = blogPosts.find(p => p.slug === slug);

  // Scroll to top when page loads or slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const menuItems = [
    { label: 'Features', href: '/#features' },
    { label: 'Blog', href: '/blog' },
    { label: 'Press', href: '/press' },
    { label: 'Pitch', href: '/pitch' },
    { label: 'Enterprise API', href: '/enterprise-api' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToContentSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      const offset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (!post) return;

    const handleScroll = () => {
      for (let i = 0; i < post.content.length; i++) {
        const section = document.getElementById(`section-${post.content[i].id}`);
        if (!section) continue;

        const titleElement = section.querySelector('h2');
        if (!titleElement) continue;

        const titleRect = titleElement.getBoundingClientRect();
        const isTitleVisible = titleRect.bottom > 0;

        if (isTitleVisible) {
          setActiveSection(post.content[i].id);
          return;
        }
      }

      if (post.content.length > 0) {
        setActiveSection(post.content[post.content.length - 1].id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{`${post.title} – Cura Blog`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://mycura.org/blog/${post.slug}`} />
        <meta property="og:title" content={`${post.title} – Cura Blog`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={`https://mycura.org/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.image} />
        <meta property="article:published_time" content={post.date} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} – Cura Blog`} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.image,
            "datePublished": post.date,
            "url": `https://mycura.org/blog/${post.slug}`,
            "publisher": { "@type": "Organization", "name": "Cura", "url": "https://mycura.org" },
            "isPartOf": { "@type": "Blog", "name": "Cura Blog", "url": "https://mycura.org/blog" }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mycura.org/" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://mycura.org/blog" },
              { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://mycura.org/blog/${post.slug}` }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-50 selection:bg-blue-200 selection:text-blue-900">
        <Navigation
          menuItems={menuItems}
          scrollToSection={scrollToSection}
          scrollToTop={scrollToTop}
          forceWhiteBackground={true}
        />

        <main className="pt-40 pb-24">
          {/* Back Link */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors group font-medium"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span>Back to Journal</span>
            </Link>
          </div>

          {/* Hero Section - Image Only */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-12">
            <motion.div
              style={{ opacity: heroOpacity, scale: heroScale }}
              className="relative rounded-[2.5rem] overflow-hidden bg-slate-900 aspect-[16/10] md:aspect-[21/9] shadow-2xl mb-12"
            >
              <img
                src={post.image}
                alt={post.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </motion.div>
          </section>

          {/* Content with Sidebar */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
              {/* Sidebar: Metadata, Excerpt & Table of Contents */}
              <motion.aside
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="hidden lg:block w-72 flex-shrink-0"
              >
                <div className="sticky top-36">
                  {/* Metadata Section */}
                  <div className="mb-8 space-y-6">
                    <div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider rounded-full">
                        {post.category}
                      </span>
                    </div>

                    <div className="space-y-3 text-sm font-medium text-slate-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {post.readTime}
                      </div>
                    </div>
                  </div>

                  {/* Excerpt in Sidebar */}
                  <div className="mb-12 pb-8 border-b border-slate-200">
                    <p className="text-base text-slate-600 leading-relaxed font-medium">
                      {post.excerpt}
                    </p>
                  </div>

                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-6">
                    Table of Contents
                  </h3>
                  <nav className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-200" />
                    <ul className="space-y-4">
                      {post.content.map((section) => (
                        <li key={section.id} className="relative pl-6">
                          <button
                            onClick={() => scrollToContentSection(section.id)}
                            className={`text-sm text-left transition-colors duration-200 ${activeSection === section.id
                              ? 'text-blue-600 font-semibold'
                              : 'text-slate-500 hover:text-slate-900'
                              }`}
                          >
                            <span className={`absolute left-0 top-1.5 w-0.5 h-4 bg-blue-600 transition-opacity duration-200 ${activeSection === section.id ? 'opacity-100' : 'opacity-0'
                              }`} />
                            {section.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </nav>

                  <div className="mt-12 pt-8 border-t border-slate-200">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
                      Share this article
                    </h4>
                    <div className="relative">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.href);
                          const btn = document.getElementById('copy-btn');
                          if (btn) {
                            const originalContent = btn.innerHTML;
                            btn.innerHTML = '<span class="flex items-center gap-2 text-green-600"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Copied!</span>';
                            setTimeout(() => {
                              btn.innerHTML = originalContent;
                            }, 2000);
                          }
                        }}
                        id="copy-btn"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 text-sm font-medium w-full justify-center group"
                      >
                        <Share2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        Copy Link
                      </button>
                    </div>
                  </div>
                </div>
              </motion.aside>

              {/* Article Content */}
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex-1 max-w-3xl mx-auto lg:mx-0"
              >
                {/* Desktop Title */}
                <h1 className="hidden lg:block text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-10 leading-tight">
                  {post.title}
                </h1>

                {/* Mobile Metadata & Title */}
                <div className="lg:hidden mb-12">
                  <div className="flex flex-wrap items-center gap-3 mb-6 text-sm font-medium">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider rounded-full">
                      {post.category}
                    </span>
                    <span className="flex items-center gap-1.5 text-slate-500">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1.5 text-slate-500">
                      <Clock className="w-3.5 h-3.5" />
                      {post.readTime}
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mb-6 leading-tight" role="presentation" aria-hidden="true">
                    {post.title}
                  </div>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {post.excerpt}
                  </p>
                </div>
                {post.content.map((section) => (
                  <section
                    key={section.id}
                    id={`section-${section.id}`}
                    className="mb-16 scroll-mt-32"
                  >
                    <h2 className="text-2xl md:text-3xl text-slate-900 mb-6">
                      {section.title}
                    </h2>

                    {/* Highlights */}
                    {section.highlights && section.highlights.length > 0 && (
                      <div className="mb-8 grid gap-4">
                        {section.highlights.map((highlight, hIndex) => (
                          <div
                            key={hIndex}
                            className="flex gap-4 bg-blue-50/50 border border-blue-100 p-5 rounded-2xl"
                          >
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                              <Lightbulb className="w-5 h-5" />
                            </div>
                            <p className="text-slate-700 font-medium leading-relaxed pt-1.5">
                              {renderFormattedText(highlight)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="prose prose-lg prose-slate max-w-none">
                      {section.content.split('\n\n').map((paragraph, pIndex) => (
                        <p key={pIndex} className="text-slate-600 leading-relaxed mb-6 text-lg">
                          {renderFormattedText(paragraph)}
                        </p>
                      ))}
                    </div>

                    {/* Disclaimer */}
                    {section.disclaimer && (
                      <div className="mt-8 flex gap-4 bg-slate-50 border border-slate-100 p-5 rounded-xl">
                        <AlertCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <p className="text-slate-500 text-sm leading-relaxed italic">
                          <span className="font-semibold not-italic">Note:</span> {section.disclaimer}
                        </p>
                      </div>
                    )}

                    {/* Call to Action Button */}
                    {section.callToAction && (
                      <div className="mt-10 flex justify-center">
                        <a
                          href={section.callToAction.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-full font-semibold text-lg shadow-lg shadow-blue-600/20 hover:bg-blue-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                        >
                          {section.callToAction.text}
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </a>
                      </div>
                    )}
                  </section>
                ))}
              </motion.article>
            </div>
          </section>

          {/* Related Articles */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mt-12 pt-16 border-t border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Continue Reading</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {blogPosts
                .filter(p => p.id !== post.id)
                .slice(0, 2)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-slate-100 flex flex-col md:flex-row h-full md:h-48"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    <div className="md:w-48 h-48 md:h-full flex-shrink-0 overflow-hidden relative">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-6 flex flex-col justify-center">
                      <span className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">
                        {relatedPost.category}
                      </span>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-slate-500 text-sm line-clamp-2 mb-3">
                        {relatedPost.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
                        <Clock className="w-3.5 h-3.5" />
                        {relatedPost.readTime}
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};
